/**
 * Post-processing facade (S4-03, D14). The chain itself (postfxChain.ts, with the `postprocessing` library, ~18 kB
 * gzip) is not in the start bundle: it loads after the first frame. Until then — and until the scene has compiled its
 * HDR variants — everything renders plainly, as on the 'low' tier; the scene then switches to its tier and the bloom
 * fades in.
 */
import type * as THREE from 'three';
import type { Quality } from '../core/quality';
import type { PostFxChain } from './postfxChain';

export class PostFx {
  private chain: PostFxChain | null = null;
  private loading: Promise<PostFxChain> | null = null;
  private tier: Quality = 'low';
  private width = 1;
  private height = 1;

  constructor(
    private readonly renderer: THREE.WebGLRenderer,
    private readonly scene: THREE.Scene,
    private readonly camera: THREE.Camera,
    private readonly stars: THREE.Object3D,
    private readonly starCamera: THREE.Camera,
  ) {}

  /** The tier actually rendered: 'low' until the chain has loaded. */
  get quality(): Quality {
    return this.chain ? this.tier : 'low';
  }

  /** Loads the chain (once). */
  load(): Promise<PostFxChain> {
    return (this.loading ??= import('./postfxChain').then((m) => {
      const chain = new m.PostFxChain(this.renderer, this.scene, this.camera, this.stars, this.starCamera);
      chain.setSize(this.width, this.height);
      chain.setQuality(this.tier);
      this.chain = chain;
      return chain;
    }));
  }

  /** Switches the tier; a post-processed tier takes effect once the chain has loaded. */
  setQuality(tier: Quality): void {
    this.tier = tier;
    this.chain?.setQuality(tier);
  }

  setSize(width: number, height: number): void {
    this.width = width;
    this.height = height;
    this.chain?.setSize(width, height);
  }

  /** Renders one frame (stars, then the scene on top). */
  render(dt: number): void {
    if (this.chain) {
      this.chain.render(dt);
      return;
    }
    const r = this.renderer;
    r.clear();
    r.render(this.stars, this.starCamera);
    r.clearDepth();
    r.render(this.scene, this.camera);
  }

  dispose(): void {
    this.chain?.dispose();
  }

  /** The half-float target the scene renders into on the post-processed tiers (loads the chain). */
  async sceneTarget(): Promise<THREE.WebGLRenderTarget> {
    return (await this.load()).sceneTarget;
  }

  /** Warms the chain up for a post-processed tier (loads the chain); see PostFxChain.warmUp. */
  async warmUp(
    tier: Exclude<Quality, 'low'>,
    between: () => Promise<void>,
    asShown: (step: () => void) => void,
  ): Promise<void> {
    await (await this.load()).warmUp(tier, between, asShown);
  }
}
